import {
  Checkbox,
  SelectInput,
  SelectInputItem,
  type SelectInputProps,
  TagSelect,
  Tooltip,
} from '@ads/components-react';
import { ExclamationCircle } from '@ads/icons';
import { useTranslation } from '@linka/i18n';
import { cn } from '@linka/utils';
import { useEffect, useState } from 'react';
import { useIntersectionObserver } from '../../hooks/use-intersection-observer';

type Option = {
  value: string;
  label: string;
  description?: string;
  tooltipText?: string;
  disabled?: boolean;
};

export interface MultiSelectProps extends Omit<SelectInputProps, 'onValueChange' | 'defaultValue'> {
  options: Option[];
  containerSelectedClassName?: string;
  onValueChange?: (option: string[]) => void;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  defaultValue?: string[];
  hideOptions?: boolean;
  optionsLimit?: number;
}

export const MultiSelect = ({
  options,
  containerSelectedClassName,
  onValueChange,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  defaultValue,
  hideOptions = false,
  optionsLimit = 15,
  ...props
}: MultiSelectProps) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslation();
  const checkIsSelected = (value: string) => selected.includes(value);

  const { isIntersecting, ref } = useIntersectionObserver({ threshold: 0.5 });

  const toggleOption = (value: string) => {
    const newSelectedValue = checkIsSelected(value)
      ? selected.filter((selectedOption) => selectedOption !== value)
      : [...selected, value];

    setSelected(newSelectedValue);
    onValueChange?.(newSelectedValue);
  };

  useEffect(() => {
    if (!defaultValue) return;
    setSelected(defaultValue);
    if (isIntersecting && fetchNextPage && !isFetchingNextPage) fetchNextPage();
  }, [defaultValue, isIntersecting, fetchNextPage, isFetchingNextPage]);

  return (
    <div className='flex flex-col gap-micro'>
      <SelectInput
        {...props}
        fullWidth
        size='md'
        custom={{ content: '!z-30', ...props.custom }}
        onOpenChange={(isOpen) => isOpen && setIsOpen(true)}
        innerProps={{
          content: {
            onPointerDownOutside: () => setIsOpen(false),
            onEscapeKeyDown: () => setIsOpen(false),
          },
        }}
        className='relative'
        value=''
        open={isOpen}
      >
        {options.length === 0 ? (
          <p className='py-xxs text-center ts-paragraph-xxs'>{t('empty')}</p>
        ) : (
          options.map((option) => (
            <SelectInputItem
              onMouseDown={() => !option.disabled && toggleOption(option.value)}
              className={cn({ 'pointer-events-none': option.disabled })}
              custom={{
                root: '!h-auto [&>span]:line-clamp-none',
              }}
              disabled={option.disabled}
              value={option.value}
              key={option.value}
            >
              <div className='flex flex-wrap justify-between w-full items-center p-nano'>
                {isOpen && (
                  <Checkbox
                    disabled={option.disabled}
                    className='[&>span]:whitespace-normal [&>span]:break-words'
                    checked={checkIsSelected(option.value)}
                    label={option.label}
                  />
                )}

                {option.description && <p className='w-full pl-xxs'>{option.description}</p>}

                {option.disabled && option.tooltipText && (
                  <Tooltip title={option.tooltipText}>
                    <ExclamationCircle className='opacity-intense pointer-events-auto size-[1em]' />
                  </Tooltip>
                )}
              </div>
            </SelectInputItem>
          ))
        )}
        {hasNextPage && (
          <p className='ts-paragraph-xxxs text-center text-gray-500' ref={ref}>
            {t('loading')}...
          </p>
        )}
      </SelectInput>

      {selected.length > 0 && !hideOptions && (
        <ul className={cn('flex items-center flex-wrap gap-quark pb-mini sm:pb-0', containerSelectedClassName)}>
          {selected.slice(0, optionsLimit).map((selectedOption) => {
            const option = options.find((option) => option.value === selectedOption);
            if (option?.label === undefined) return null;

            return (
              <li key={selectedOption}>
                {option?.disabled && option.tooltipText ? (
                  <Tooltip title={option.tooltipText}>
                    <div>
                      <TagSelect
                        ariaLabelRemovable={t('remove')}
                        onClickRemovable={() => toggleOption(selectedOption)}
                        disabled={option?.disabled}
                        className='[&>p]:truncate [&>p]:max-w-[20rem]'
                        label={`${option?.label}`}
                        mode='removable'
                        size='sm'
                      />
                    </div>
                  </Tooltip>
                ) : (
                  <TagSelect
                    ariaLabelRemovable={t('remove')}
                    onClickRemovable={() => toggleOption(selectedOption)}
                    disabled={option?.disabled}
                    className='[&>p]:truncate [&>p]:max-w-[20rem]'
                    label={`${option?.label}`}
                    mode='removable'
                    size='sm'
                  />
                )}
              </li>
            );
          })}

          <span className='relative ts-caption-xxs'>
            {selected.length > optionsLimit && <p>+{selected.length - optionsLimit}</p>}
          </span>
        </ul>
      )}
    </div>
  );
};
